// File: `src/features/variant_workflow/molecular_impact/SilicoToolModal.tsx`
// Purpose: Modal used to ADD a new in silico prediction tool entry for the current variant.
// - The user selects a tool from the global tools list and can optionally provide score/result/comment.
// - The entry is created on the server and then the table in SilicoPredictionSection is refreshed.
// - The backend assigns the category for the tool (commonly "Functional" for the tools added here).
import { Alert, Button, Input, Select } from 'antd';
import { EditorState } from 'draft-js';
import { Formik } from 'formik';
import React from 'react';
import { useSelector } from 'react-redux';

import FullPageLoader from '../../../foundation/components/full_page_loader/FullPageLoader';
import Modal from '../../../foundation/components/modal/Modal';
import RichTextEditor from '../../../foundation/components/rich_text_editor/RichTextEditor';
import { useVariantAccess } from '../hooks/useVariantAccess';
import { selectVariantLock } from '../redux/selectors';
import { silicoToolAddValidationSchema } from '../validation_schemas/silico_tool_add_validation_schema';
import { useSilicoToolModalLogic } from './hooks';

interface SilicoToolModalProps {
  variant_id: string;
  open: boolean;
  closeModal: () => void;
}

const SilicoToolModal: React.FC<SilicoToolModalProps> = ({
  variant_id,
  open,
  closeModal,
}) => {
  const { isReadOnly, canInteract } = useVariantAccess();
  const lock = useSelector(selectVariantLock);

  // All logic extracted to custom hook for separation of concerns
  const {
    isSubmitting,
    selectOptions,
    modalOpts: hookModalOpts,
    initialValues,
    getIsSaveDisabled,
    getPredictionResultConfig,
    handleSubmit,
    selectLabelFilterOption,
  } = useSilicoToolModalLogic({ variant_id, closeModal });

  // Override open from hook with prop value
  const modalOpts = { ...hookModalOpts, open };

  return (
    <Modal {...modalOpts} className="h-proband-modal">
      {isSubmitting && <FullPageLoader />}

      {isReadOnly && lock && (
        <Alert
          message="Read-Only Mode"
          description={`This variant is currently locked by ${lock.locked_by} and cannot be edited.`}
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      <Formik
        initialValues={initialValues}
        validationSchema={silicoToolAddValidationSchema}
        onSubmit={handleSubmit}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleSubmit,
          setFieldValue,
          handleBlur,
          dirty,
          // add isValid to enable the disable logic for the save button
          isValid,
        }) => {
          // Centralized disable logic via getIsSaveDisabled
          const isSaveDisabled = getIsSaveDisabled(isValid, dirty);

          // Derive selected tool and choices from current form value (no extra state)
          const { predictionResultChoices, shouldUsePredictionResultSelect } =
            getPredictionResultConfig(values.predictiontool_id as any);

          return (
            <form onSubmit={handleSubmit}>
              <div className="c-form-field">
                <div className="c-form-field__label">
                  Prediction tool: <span style={{ color: 'red' }}>*</span>
                </div>
                <div className="c-form-field__wrapper">
                  <Select
                    value={values.predictiontool_id}
                    onChange={(v) => {
                      void setFieldValue('predictiontool_id', v);
                      void setFieldValue('prediction_result', ''); // reset on tool change
                    }}
                    onBlur={() => handleBlur('predictiontool_id')}
                    className="h-proband-modal__select-field"
                    options={selectOptions}
                    placeholder="Select prediction tool"
                    showSearch
                    filterOption={selectLabelFilterOption}
                    disabled={!canInteract}
                  />
                  <div className="c-form-field__error">
                    {errors.predictiontool_id && touched.predictiontool_id
                      ? (errors.predictiontool_id as any)
                      : undefined}
                  </div>
                </div>
              </div>

              {/* Score vor Prediction result */}
              <div className="c-form-field">
                <div className="c-form-field__label">Score:</div>
                <div className="c-form-field__wrapper">
                  <Input
                    name="score"
                    value={values.score}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="Numeric value, e.g. 0.87"
                    readOnly={!canInteract}
                    status={errors.score ? 'error' : undefined}
                  />
                  <div
                    className={`c-form-field__hint${
                      errors.score ? ' c-form-field__hint--error' : ''
                    }`}
                  >
                    {errors.score ||
                      'Expected format: integer or decimal (e.g. 0.943, 101)'}
                  </div>
                </div>
              </div>

              <div className="c-form-field">
                <div className="c-form-field__label">Prediction result:</div>
                <div className="c-form-field__wrapper">
                  {shouldUsePredictionResultSelect ? (
                    <Select
                      value={values.prediction_result || undefined}
                      onChange={(v) => {
                        void setFieldValue('prediction_result', v);
                      }}
                      onBlur={() => handleBlur('prediction_result')}
                      className="h-proband-modal__select-field"
                      options={predictionResultChoices}
                      placeholder="Select prediction result"
                      showSearch
                      filterOption={(input, option) =>
                        String(option?.label ?? '')
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      }
                      disabled={!canInteract}
                    />
                  ) : (
                    <Input
                      name="prediction_result"
                      value={values.prediction_result}
                      onChange={handleChange}
                      placeholder="e.g. Damaging"
                      readOnly={!canInteract}
                    />
                  )}
                  <div className="c-form-field__error">
                    {errors.prediction_result && touched.prediction_result
                      ? errors.prediction_result
                      : undefined}
                  </div>
                </div>
              </div>

              <div className="c-form-field">
                <div className="c-form-field__label">Comment:</div>
                <div className="c-form-field__wrapper">
                  <RichTextEditor
                    name="comment"
                    editorState={values.comment ?? EditorState.createEmpty()}
                    onChange={(newState) =>
                      void setFieldValue('comment', newState)
                    }
                    onBlur={() => handleBlur('comment')}
                    placeholder=""
                    label=""
                    readOnly={!canInteract}
                  />
                  <div className="c-form-field__error">
                    {errors.comment ? (errors.comment as any) : undefined}
                  </div>
                </div>
              </div>

              <div className="c-form__btn-wrapper h-proband-modal__form-btn-wrapper">
                {/* Standardisierte Disable-Logik + einheitliche Button-Klasse */}
                <Button
                  type="primary"
                  htmlType="submit"
                  className="h-proband-modal__form-submit-btn"
                  disabled={isSaveDisabled}
                >
                  Save changes
                </Button>
              </div>
            </form>
          );
        }}
      </Formik>
    </Modal>
  );
};

export default SilicoToolModal;
